/**
 * Serviço de Comunicação com Dispositivos
 * 
 * Implementação da comunicação com o ESP32 - Caixa de Medicamentos via Bluetooth Low Energy (BLE)
 * 
 * Configuração para ESP32:
 * - Nome do dispositivo: CaixaMedicamentosESP32
 * - Service UUID: 6e400001-b5a3-f393-e0a9-e50e24dcca9e
 * - Characteristic RX (para escrita): 6e400002-b5a3-f393-e0a9-e50e24dcca9e
 * - Characteristic TX (para notificação): 6e400003-b5a3-f393-e0a9-e50e24dcca9e
 * 
 * Protocolo de comunicação:
 * - Enviar comando: COMPARTIMENTO:X (onde X é o número do compartimento 1-9)
 * - Resposta esperada: "OK: Compartimento X agendado." ou "ERRO: Compartimento inválido"
 */

import api from "./api";

// Constantes para comunicação com dispositivos
const SERIAL_BAUD_RATE = 115200;  // Taxa de transferência para ESP32
// UUIDs específicos para o ESP32 CaixaMedicamentos
const BLUETOOTH_SERVICE_UUID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const BLUETOOTH_CHARACTERISTIC_RX = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';
const BLUETOOTH_CHARACTERISTIC_TX = '6e400003-b5a3-f393-e0a9-e50e24dcca9e';
const WIFI_DISCOVERY_PORT = 9898; // Porta para descoberta de dispositivos ESP32 na rede

// Simular a conexão com dispositivos - em produção, isso seria substituído por comunicação real
const DISPOSITIVOS_MOCKADOS = [
  { id: "esp32_01", nome: "ESP32 Sala", tipo: "ESP32", ultimaConexao: "2023-10-15T14:30:00" },
  { id: "arduino_01", nome: "Arduino Cozinha", tipo: "Arduino UNO", ultimaConexao: "2023-10-10T09:15:00" }
];

// Buscar dispositivos disponíveis
export const buscarDispositivosDisponiveis = async (metodoConexao) => {
  try {
    let dispositivos = [];

    switch (metodoConexao) {
      case "serial":
        dispositivos = await buscarDispositivosSerial();
        break;
      case "bluetooth":
        dispositivos = await buscarDispositivosBluetooth();
        break;
      case "wifi":
        dispositivos = await buscarDispositivosWifi();
        break;
      default:
        throw new Error("Método de conexão não suportado");
    }

    return dispositivos;
  } catch (error) {
    console.error("Erro ao buscar dispositivos:", error);
    throw error;
  }
};

// Buscar dispositivos por porta serial
const buscarDispositivosSerial = async () => {
  try {
    // Verificar se a API SerialPort está disponível no navegador
    if (!navigator.serial) {
      throw new Error("Seu navegador não suporta a API Web Serial. Use Chrome ou Edge.");
    }

    // Solicitar porta
    const ports = await navigator.serial.getPorts();
    let dispositivos = [];

    // Se não houver portas já autorizadas, solicitar acesso a uma nova porta
    if (ports.length === 0) {
      try {
        await navigator.serial.requestPort();
        const novaPorts = await navigator.serial.getPorts();
        ports.push(...novaPorts);
      } catch (err) {
        // Usuário cancelou a seleção
        console.log("Usuário cancelou a seleção de porta serial");
      }
    }

    // Mapear as portas para o formato de dispositivos
    dispositivos = ports.map((port, index) => ({
      id: `serial_${index}`,
      nome: `Dispositivo Serial ${index + 1}`,
      tipo: "ESP32",
      porta: port,
      ultimaConexao: new Date().toISOString()
    }));

    return dispositivos;
  } catch (error) {
    console.error("Erro ao buscar dispositivos seriais:", error);
    throw error;
  }
};

// Buscar dispositivos Bluetooth
const buscarDispositivosBluetooth = async () => {
  try {
    // Verificar se a API Bluetooth está disponível no navegador
    if (!navigator.bluetooth) {
      throw new Error("Seu navegador não suporta a API Web Bluetooth. Use Chrome ou Edge.");
    }

    // Solicitar dispositivo Bluetooth
    const device = await navigator.bluetooth.requestDevice({
      // Procurar pelo dispositivo específico "CaixaMedicamentosESP32"
      filters: [
        { name: "CaixaMedicamentosESP32" },
        { services: [BLUETOOTH_SERVICE_UUID] }
      ],
      optionalServices: [BLUETOOTH_SERVICE_UUID]
    });

    // Retornar dispositivo encontrado
    return [{
      id: device.id,
      nome: device.name || "CaixaMedicamentosESP32",
      tipo: "ESP32",
      device: device,
      ultimaConexao: new Date().toISOString()
    }];
  } catch (error) {
    console.error("Erro ao buscar dispositivos bluetooth:", error);
    // Se o usuário cancelou a seleção, retornar array vazio em vez de erro
    if (error.name === 'NotFoundError' || error.message.includes('User cancelled')) {
      return [];
    }
    throw error;
  }
};

// Buscar dispositivos na rede WiFi
const buscarDispositivosWifi = async () => {
  try {
    // Em um ambiente real, podemos fazer uma busca multicast na rede local
    // ou ter um servidor de descoberta, mas aqui vamos simplificar com uma
    // busca baseada em API ou mDNS

    // Opção 1: Podemos usar a API do backend para encontrar dispositivos na rede
    const response = await api.get('/DispositivoArduino/descobrir');
    return response.data.map(device => ({
      id: device.id,
      nome: device.nome,
      tipo: "ESP32",
      ip: device.ip,
      porta: device.porta || 80,
      ultimaConexao: device.ultimaConexao || new Date().toISOString()
    }));
    
    // Se a API backend não existir ainda, podemos usar este código mock:
    /*
    return [
      { 
        id: "esp32_wifi_01", 
        nome: "ESP32 WiFi", 
        tipo: "ESP32", 
        ip: "192.168.1.100", 
        porta: 80,
        ultimaConexao: new Date().toISOString() 
      }
    ];
    */
  } catch (error) {
    console.error("Erro ao buscar dispositivos WiFi:", error);
    // Se a API ainda não está implementada, retornar dispositivo mock
    return [
      { 
        id: "esp32_wifi_01", 
        nome: "ESP32 WiFi", 
        tipo: "ESP32", 
        ip: "192.168.1.100", 
        porta: 80,
        ultimaConexao: new Date().toISOString() 
      }
    ];
  }
};

// Conectar ao dispositivo
export const conectarDispositivo = async (dispositivo, metodoConexao) => {
  try {
    let conexao;

    switch (metodoConexao) {
      case "serial":
        conexao = await conectarSerial(dispositivo);
        break;
      case "bluetooth":
        conexao = await conectarBluetooth(dispositivo);
        break;
      case "wifi":
        conexao = await conectarWifi(dispositivo);
        break;
      default:
        throw new Error("Método de conexão não suportado");
    }

    return {
      sucesso: true,
      dispositivo: {
        ...dispositivo,
        conexao,
        status: "conectado",
        metodoConexao,
        ultimaConexao: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error("Erro ao conectar dispositivo:", error);
    throw error;
  }
};

// Conectar via Serial
const conectarSerial = async (dispositivo) => {
  try {
    const port = dispositivo.porta;
    await port.open({ baudRate: SERIAL_BAUD_RATE });

    // Configurar os streams para comunicação com a porta
    const textEncoder = new TextEncoder();
    const textDecoder = new TextDecoder();
    
    // Salvar referências do reader e writer
    const writer = port.writable.getWriter();
    const reader = port.readable.getReader();

    // Adicionar um método de envio de dados
    const enviarComando = async (comando) => {
      const data = textEncoder.encode(comando + '\n');
      await writer.write(data);
    };

    // Adicionar um método de leitura de dados
    const lerResposta = async (timeoutMs = 5000) => {
      return new Promise(async (resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Timeout ao aguardar resposta do dispositivo"));
        }, timeoutMs);

        try {
          const { value, done } = await reader.read();
          clearTimeout(timeout);
          
          if (done) {
            reject(new Error("Conexão serial fechada"));
          } else {
            resolve(textDecoder.decode(value));
          }
        } catch (error) {
          clearTimeout(timeout);
          reject(error);
        }
      });
    };

    // Testar a conexão
    await enviarComando("PING");
    const resposta = await lerResposta();
    
    if (!resposta.includes("PONG")) {
      throw new Error("Dispositivo não respondeu corretamente");
    }

    return {
      port,
      reader,
      writer,
      enviarComando,
      lerResposta,
      fechar: async () => {
        reader.releaseLock();
        writer.releaseLock();
        await port.close();
      }
    };
  } catch (error) {
    console.error("Erro ao conectar serial:", error);
    throw error;
  }
};

// Conectar via Bluetooth
const conectarBluetooth = async (dispositivo) => {
  try {
    const device = dispositivo.device;
    const server = await device.gatt.connect();
    
    // Obter o serviço
    const service = await server.getPrimaryService(BLUETOOTH_SERVICE_UUID);
    
    // Obter as características para comunicação
    const characteristicRX = await service.getCharacteristic(BLUETOOTH_CHARACTERISTIC_RX);
    const characteristicTX = await service.getCharacteristic(BLUETOOTH_CHARACTERISTIC_TX);
    
    // Configurar notificações (para receber dados do dispositivo)
    await characteristicTX.startNotifications();
    
    // Buffer para armazenar dados recebidos
    let dadosRecebidos = "";
    
    // Adicionar listener para dados recebidos
    const onDadosRecebidos = (event) => {
      const valor = new TextDecoder().decode(event.target.value);
      dadosRecebidos += valor;
      
      console.log("Dados recebidos do ESP32:", valor);
    };
    
    characteristicTX.addEventListener('characteristicvaluechanged', onDadosRecebidos);
    
    // Método para enviar comandos
    const enviarComando = async (comando, parametro = "") => {
      const mensagemCompleta = parametro ? `${comando}:${parametro}` : comando;
      console.log("Enviando para ESP32:", mensagemCompleta);
      
      const encoder = new TextEncoder();
      const data = encoder.encode(mensagemCompleta);
      await characteristicRX.writeValue(data);
    };
    
    // Método para ler resposta
    const lerResposta = async (timeoutMs = 5000) => {
      return new Promise((resolve, reject) => {
        // Limpar dados anteriores
        dadosRecebidos = "";
        
        const timeout = setTimeout(() => {
          reject(new Error("Timeout ao aguardar resposta do dispositivo"));
        }, timeoutMs);
        
        const checarResposta = setInterval(() => {
          if (dadosRecebidos.length > 0) {
            clearInterval(checarResposta);
            clearTimeout(timeout);
            resolve(dadosRecebidos);
            dadosRecebidos = "";
          }
        }, 100);
      });
    };
    
    // Testar a conexão - não enviamos PING, pois o ESP32 não responde a este comando
    // Apenas verificamos se conseguimos estabelecer a conexão
    
    return {
      device,
      server,
      service,
      characteristicRX,
      characteristicTX,
      enviarComando,
      lerResposta,
      fechar: async () => {
        characteristicTX.removeEventListener('characteristicvaluechanged', onDadosRecebidos);
        await characteristicTX.stopNotifications();
        server.disconnect();
      }
    };
  } catch (error) {
    console.error("Erro ao conectar bluetooth:", error);
    throw error;
  }
};

// Conectar via WiFi
const conectarWifi = async (dispositivo) => {
  try {
    const { ip, porta } = dispositivo;
    
    // Testar a conexão com o dispositivo ESP32
    const response = await fetch(`http://${ip}:${porta}/ping`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      timeout: 5000 // 5 segundos de timeout
    });
    
    if (!response.ok) {
      throw new Error(`Falha ao conectar: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    if (!data.status || data.status !== "ok") {
      throw new Error("Dispositivo não respondeu corretamente");
    }
    
    // Método para enviar comandos
    const enviarComando = async (comando, params = {}) => {
      const response = await fetch(`http://${ip}:${porta}/comando`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          comando,
          ...params
        }),
        timeout: 5000
      });
      
      if (!response.ok) {
        throw new Error(`Erro ao enviar comando: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    };
    
    return {
      ip,
      porta,
      enviarComando,
      fechar: async () => {
        // No caso de WiFi, não há conexão persistente para fechar
        // Mas podemos notificar o dispositivo que estamos desconectando
        try {
          await enviarComando("desconectar");
        } catch (e) {
          console.log("Erro ao desconectar WiFi:", e);
        }
      }
    };
  } catch (error) {
    console.error("Erro ao conectar WiFi:", error);
    throw error;
  }
};

// Desconectar do dispositivo
export const desconectarDispositivo = async (dispositivo) => {
  try {
    if (dispositivo.conexao && dispositivo.conexao.fechar) {
      await dispositivo.conexao.fechar();
    }
    
    return {
      sucesso: true,
      mensagem: "Dispositivo desconectado com sucesso"
    };
  } catch (error) {
    console.error("Erro ao desconectar dispositivo:", error);
    throw error;
  }
};

// Enviar programação para o dispositivo
export const enviarProgramacao = async (dispositivo, medicamentos) => {
  try {
    if (!dispositivo.conexao) {
      throw new Error("Dispositivo não está conectado");
    }
    
    // Baseado no método de conexão, enviar dados de forma apropriada
    switch (dispositivo.metodoConexao) {
      case "bluetooth":
        // Para o ESP32 CaixaMedicamentos, enviamos comandos no formato "COMPARTIMENTO:X"
        // onde X é o número do compartimento (1-9)
        const resultados = [];
        
        for (const medicamento of medicamentos) {
          // Assumimos que cada medicamento tem um campo "compartimento" que indica o slot (1-9)
          if (medicamento.compartimento && medicamento.compartimento >= 1 && medicamento.compartimento <= 9) {
            try {
              // Enviar comando no formato especificado
              await dispositivo.conexao.enviarComando("COMPARTIMENTO", medicamento.compartimento.toString());
              
              // Aguardar resposta
              const resposta = await dispositivo.conexao.lerResposta(5000);
              
              // Verificar se a resposta indica sucesso
              if (resposta.includes("OK:")) {
                resultados.push({
                  medicamento: medicamento.nome,
                  compartimento: medicamento.compartimento,
                  sucesso: true,
                  mensagem: resposta.trim()
                });
              } else {
                resultados.push({
                  medicamento: medicamento.nome,
                  compartimento: medicamento.compartimento,
                  sucesso: false,
                  mensagem: resposta.trim() || "Resposta inválida do dispositivo"
                });
              }
            } catch (error) {
              resultados.push({
                medicamento: medicamento.nome,
                compartimento: medicamento.compartimento,
                sucesso: false,
                mensagem: error.message
              });
            }
          } else {
            resultados.push({
              medicamento: medicamento.nome,
              compartimento: medicamento.compartimento,
              sucesso: false,
              mensagem: "Compartimento inválido. Deve ser um número entre 1 e 9."
            });
          }
        }
        
        // Verificar se pelo menos um medicamento foi programado com sucesso
        const sucessos = resultados.filter(r => r.sucesso).length;
        if (sucessos === 0) {
          throw new Error("Nenhum medicamento foi programado com sucesso");
        }
        
        return {
          sucesso: true,
          mensagem: `Programação enviada com sucesso: ${sucessos} de ${medicamentos.length} medicamentos programados.`,
          detalhes: {
            resultados,
            dataEnvio: new Date().toISOString()
          }
        };
        
      case "serial":
        // Para serial, mantemos a implementação anterior
        await dispositivo.conexao.enviarComando("PROGRAMACAO", JSON.stringify(medicamentos));
        const resposta = await dispositivo.conexao.lerResposta(10000); // 10 segundos de timeout
        
        if (!resposta.includes("OK")) {
          throw new Error("Dispositivo não confirmou recebimento da programação");
        }
        break;
        
      case "wifi":
        // Para WiFi, mantemos a implementação anterior
        const resultado = await dispositivo.conexao.enviarComando("programacao", { medicamentos });
        
        if (!resultado.sucesso) {
          throw new Error(resultado.mensagem || "Erro ao enviar programação");
        }
        break;
        
      default:
        throw new Error("Método de conexão não suportado");
    }
    
    return {
      sucesso: true,
      mensagem: `Programação enviada com sucesso para ${dispositivo.nome}`,
      detalhes: {
        totalMedicamentos: medicamentos.length,
        dataEnvio: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error("Erro ao enviar programação:", error);
    throw error;
  }
};

// Verificar status do dispositivo
export const verificarStatusDispositivo = async (dispositivo) => {
  try {
    if (!dispositivo.conexao) {
      return { status: "desconectado", bateria: 0, ultimaAtualizacao: new Date().toISOString() };
    }
    
    let statusInfo;
    
    switch (dispositivo.metodoConexao) {
      case "serial":
      case "bluetooth":
        // Para serial e bluetooth, enviamos um comando específico
        await dispositivo.conexao.enviarComando("STATUS");
        const resposta = await dispositivo.conexao.lerResposta();
        
        try {
          // Esperamos que a resposta seja um JSON
          statusInfo = JSON.parse(resposta);
        } catch (e) {
          // Se não for JSON, pelo menos verificamos se está conectado
          return { 
            status: "conectado", 
            bateria: 50, // valor padrão se não conseguirmos ler
            ultimaAtualizacao: new Date().toISOString()
          };
        }
        break;
        
      case "wifi":
        // Para WiFi, fazemos uma chamada HTTP
        const resultado = await dispositivo.conexao.enviarComando("status");
        statusInfo = resultado;
        break;
        
      default:
        throw new Error("Método de conexão não suportado");
    }
    
    return {
      status: statusInfo.status || "conectado",
      bateria: statusInfo.bateria || 50,
      ultimaAtualizacao: new Date().toISOString()
    };
  } catch (error) {
    console.error("Erro ao verificar status do dispositivo:", error);
    return { 
      status: "erro", 
      bateria: 0, 
      ultimaAtualizacao: new Date().toISOString(),
      erro: error.message
    };
  }
}; 